import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms'
import { delay, finalize, takeUntil } from 'rxjs/operators'
import { Subject } from 'rxjs'

import { FileInputComponent } from '@shared/components/form/file-upload/file-input.component'
import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { handleFormErrors } from '@core/helpers/handle-form-errors'
import { TrackedUnsavedForm, UnsavedFormTracker } from '@core/services/unsaved-form-tracker.service'
import { listRefreshDelay } from '@app/core/constants/list-refresh-delay'
import { ServiceRecordInput, ServiceRecordRow } from '@shared/common-components/service-record/service-record.model'
import { ServiceRecordService } from '@shared/common-components/service-record/service-record.service'
import dayjs from 'dayjs'

@Component({
  selector: 'lin-service-record-form',
  templateUrl: './service-record-form.component.html',
  styles: [
    `
      .textarea {
        resize: none;
      }

      .submit-button {
        margin-top: 1.25em;
      }
    `,
  ],
  standalone: false,
})
export class ServiceRecordFormComponent implements OnInit, OnChanges, OnDestroy, TrackedUnsavedForm {
  form: FormGroup
  isSubmitting = false
  formError: string
  isActualDirty = false

  @Input() record: ServiceRecordRow = null
  @Input() reminderId: number
  @Input() mileage = 0
  @Input() mileageRequired = false
  @Input() engineHours = 0
  @Input() engineHoursRequired = false
  @Input() disabled: boolean
  @Output() recordAdded = new EventEmitter<ServiceRecordRow>()
  @Output() submitting = new EventEmitter<boolean>()
  @ViewChild(FormGroupDirective, { static: true }) formDirective: FormGroupDirective
  @ViewChildren(FileInputComponent) fileInputs: QueryList<FileInputComponent>

  private readonly onDestroy$ = new Subject<void>()
  isFormChanged: ReturnType<typeof takeFormSnapshot>

  constructor(
    private formBuilder: FormBuilder,
    private serviceRecordService: ServiceRecordService,
    private unsavedFormTrackerService: UnsavedFormTracker,
  ) {}

  ngOnInit() {
    this.unsavedFormTrackerService.track(this)
    this.initForm()
  }

  ngOnChanges({ mileageRequired, engineHoursRequired }: SimpleChanges) {
    mileageRequired && this.setRequiredState('odometer', <boolean>mileageRequired.currentValue)
    engineHoursRequired && this.setRequiredState('engineHours', <boolean>engineHoursRequired.currentValue)
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      date: ['', Validators.required],
      odometer: '',
      engineHours: '',
      note: '',
      cost: ['', Validators.min(0)],
      files: [[]],
      removeFiles: [[]],
    })

    this.mileageRequired && this.setRequiredState('odometer', true)
    this.engineHoursRequired && this.setRequiredState('engineHours', true)

    this.initFormValues()

    const valueChanges$ = this.form.valueChanges.pipe(takeUntil(this.onDestroy$))
    valueChanges$.subscribe(() => (this.isActualDirty = true))

    if (this.disabled) {
      this.form.disable()
    }

    this.isFormChanged = takeFormSnapshot(this.form)
  }

  private initFormValues(): void {
    const { record } = this
    if (!record) return

    const { date, note, cost } = record

    this.form.patchValue({
      date: (date && dayjs(date).tz()) || '',
      note,
      cost,
    })

    this.form.valid && this.form.markAsDirty()
  }

  private setRequiredState(field: string, isRequired: boolean): void {
    this.form?.get(field)?.setValidators(isRequired ? Validators.required : null)
    this.form?.get(field)?.updateValueAndValidity()
  }

  removeFile(id: number): void {
    const removeFiles = this.form.get('removeFiles')
    const removeIds = removeFiles.value as number[]
    removeFiles.setValue(removeIds.concat([id]))
    removeFiles.markAsDirty()
  }

  areUploadsPossible(): boolean {
    const filesCount = (this.form.get('files').value as File[]).length
    const removeFilesCount = (this.form.get('removeFiles').value as File[]).length
    const { record } = this
    const uploadedFilesCount = (record && record.files && record.files.length) || 0

    return filesCount + uploadedFilesCount - removeFilesCount < 5
  }
  prepareDataForSave(): ServiceRecordInput {
    const { value } = this.form as { value: ServiceRecordInput }
    const { date, odometer, engineHours, note, cost, files } = value

    const nonEmptyFiles = files.filter(Boolean)
    const toKilometers = (mileageM: any) => {
      const num = Number(mileageM)
      return isNaN(num) ? null : num * 1000
    }

    return {
      date: date && dayjs(date).tz().hour(0).minute(0).second(0).millisecond(0).format(),
      ...(engineHours && { engineHours: Number(engineHours) }),
      ...(odometer && !isNaN(Number(odometer)) && { odometer: toKilometers(odometer) }),
      note,
      cost,
      files: nonEmptyFiles,
    }
  }

  resetForm() {
    this.formError = ''
    this.record = null
    this.formDirective.resetForm()
    this.form.reset({
      date: '',
      odometer: '',
      engineHours: '',
      note: '',
      cost: '',
      files: [],
      removeFiles: [],
    })
    this.isFormChanged = takeFormSnapshot(this.form)
  }

  submitForm() {
    const data = this.prepareDataForSave()
    this.isSubmitting = true
    this.submitting.emit(true)

    this.serviceRecordService
      .create(this.reminderId, data)
      .pipe(
        delay(listRefreshDelay),
        finalize(() => {
          this.isSubmitting = false
          this.submitting.emit(false)
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe((serviceRecord) => {
        this.resetForm()
        this.recordAdded.emit(serviceRecord)
      }, handleFormErrors(this.form))
  }

  ngOnDestroy() {
    this.unsavedFormTrackerService.discard(this)
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
